import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PersonaShell from './persona-shell';
import Spinner from '../../components/common/spinner';
import {
  CreateEvalRunPayload,
  EvalFlag,
  EvalProposal,
  EvalProposalDiff,
  EvalRun,
  EvalRunStatus,
  EvalScenarioResult,
  ruleScope,
  useAcceptEvalProposal,
  useCancelEvalRun,
  useCreateEvalRun,
  useEvalRun,
  usePersonaEvalRuns,
  usePersonaRules,
} from '../../hooks/usePersonaAdmin';
import { ROUTE_PATH } from '../../routes/paths';

const statusClass = (status: EvalRunStatus) => {
  switch (status) {
    case 'complete':
      return 'bg-green text-black';
    case 'running':
    case 'queued':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red text-white';
    case 'cancelled':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const formatFlag = (flag: EvalFlag | string): { key: string; label: string; title?: string } => {
  if (typeof flag === 'string') return { key: flag, label: flag };
  const name = flag?.name || 'flag';
  const description = flag?.description || '';
  return {
    key: `${name}:${description}`,
    label: description ? `${name}: ${description}` : name,
    title: description || name,
  };
};

const FlagChips: React.FC<{ flags?: Array<EvalFlag | string> }> = ({ flags }) => {
  if (!flags || flags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 pl-2">
      {flags.map((flag, idx) => {
        const formatted = formatFlag(flag);
        return (
          <span
            key={`${formatted.key}-${idx}`}
            title={formatted.title}
            className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
          >
            {formatted.label}
          </span>
        );
      })}
    </div>
  );
};

const ProposalDiffBlock: React.FC<{ diff: EvalProposalDiff }> = ({ diff }) => (
  <div className="rounded border border-gray-200">
    <div className="border-b bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-600">
      {diff.field}
    </div>
    <pre className="max-h-48 overflow-auto rounded-b bg-gray-900 p-3 font-mono text-xs text-gray-100">
      {(diff.diff || '').split('\n').map((line, idx) => {
        let cls = 'text-gray-100';
        if (line.startsWith('+')) cls = 'text-green-300';
        else if (line.startsWith('-')) cls = 'text-red-300';
        else if (line.startsWith('@@')) cls = 'text-cyan-300';
        return (
          <div key={idx} className={cls}>
            {line || ' '}
          </div>
        );
      })}
    </pre>
  </div>
);

const ProposalReview: React.FC<{
  personaId: string;
  runId: number;
  proposal: EvalProposal;
}> = ({ personaId, runId, proposal }) => {
  const history = useHistory();
  const accept = useAcceptEvalProposal(personaId);
  const edits = proposal.edits || [];
  const [selected, setSelected] = useState<Set<string>>(() => new Set(edits.map((e) => e.field)));

  useEffect(() => {
    setSelected(new Set((proposal.edits || []).map((e) => e.field)));
  }, [proposal.edits]);

  if (proposal.status === 'pending' || proposal.status === 'generating') {
    return (
      <div className="mt-4 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
        Generating prompt-change proposal from failures…
      </div>
    );
  }
  if (proposal.status === 'failed') {
    return (
      <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        Prompt proposal failed{proposal.error ? `: ${proposal.error}` : '.'}
      </div>
    );
  }
  if (proposal.status === 'empty' || edits.length === 0) {
    return (
      <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        {proposal.summary || 'No prompt changes proposed.'}
      </div>
    );
  }

  const toggleField = (field: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const handleAccept = async () => {
    const version = await accept.mutateAsync({
      runId,
      payload: { accept_fields: Array.from(selected) },
    });
    history.push(ROUTE_PATH.PERSONA_PROMPTS.replace(':personaId', String(personaId)));
    return version;
  };

  const diffsByField = Object.fromEntries((proposal.diffs || []).map((d) => [d.field, d]));

  return (
    <div className="mt-4 space-y-3 rounded border border-blue-200 bg-blue-50 p-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-blue-800">Proposed prompt changes</div>
        {proposal.summary && <p className="mt-1 text-sm text-gray-700">{proposal.summary}</p>}
      </div>
      <div className="space-y-3">
        {edits.map((edit) => (
          <div key={edit.field} className="rounded border border-blue-100 bg-white p-3">
            <label className="mb-2 flex items-start gap-2 text-sm font-medium text-gray-900">
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.has(edit.field)}
                onChange={() => toggleField(edit.field)}
              />
              <span>
                Accept <span className="font-mono">{edit.field}</span>
              </span>
            </label>
            {edit.rationale && <p className="mb-2 text-xs text-gray-600">{edit.rationale}</p>}
            {diffsByField[edit.field] ? (
              <ProposalDiffBlock diff={diffsByField[edit.field]} />
            ) : (
              <pre className="max-h-40 overflow-auto rounded bg-gray-50 p-2 font-mono text-xs text-gray-700">
                {edit.proposed}
              </pre>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button
          className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={selected.size === 0 || accept.isLoading}
          onClick={handleAccept}
        >
          {accept.isLoading ? 'Creating draft…' : 'Create draft from accepted fields'}
        </button>
      </div>
    </div>
  );
};

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  const normalized = /Z$|[+-]\d\d:\d\d$/.test(raw) ? raw : `${raw}Z`;
  return new Date(normalized).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

interface LauncherProps {
  personaId: string;
  onLaunched: (run: EvalRun) => void;
}

const EvalLauncher: React.FC<LauncherProps> = ({ personaId, onLaunched }) => {
  const { data: rulesData } = usePersonaRules(personaId, true);
  const rules = rulesData?.data || [];
  const enabledIds = useMemo(() => rules.filter((r) => r.enabled).map((r) => r.id), [rules]);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [label, setLabel] = useState('');
  const [propose, setPropose] = useState(false);

  useEffect(() => {
    setSelected(new Set(enabledIds));
  }, [enabledIds]);

  const create = useCreateEvalRun(personaId);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const launch = async () => {
    const payload: CreateEvalRunPayload = {
      rule_ids: Array.from(selected),
      label: label.trim() || undefined,
      propose_prompt_changes: propose,
    };
    const run = await create.mutateAsync(payload);
    onLaunched(run);
    setLabel('');
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Launch Eval Run</h3>
        <div className="text-xs text-gray-500">
          {selected.size} / {rules.length} rule{rules.length === 1 ? '' : 's'} selected
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (optional)"
          className="rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={propose} onChange={(e) => setPropose(e.target.checked)} />
          Propose prompt changes based on failures
        </label>
      </div>

      <div className="mt-3 max-h-64 overflow-y-auto rounded border border-gray-200">
        <table className="w-full table-fixed border-collapse text-sm">
          <thead className="bg-gray-50">
            <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              <th className="w-10 py-2 pl-3"></th>
              <th className="py-2">Rule</th>
              <th className="w-24 py-2">Scope</th>
              <th className="w-24 py-2">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan={4} className="py-6 text-center text-xs text-gray-400">
                  No rules available. Create one from the Rules tab first.
                </td>
              </tr>
            )}
            {rules.map((rule) => (
              <tr key={rule.id} className="cursor-pointer border-b hover:bg-gray-50" onClick={() => toggle(rule.id)}>
                <td className="py-2 pl-3">
                  <input
                    type="checkbox"
                    checked={selected.has(rule.id)}
                    onChange={() => toggle(rule.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className="py-2 font-medium text-gray-900">{rule.slug}</td>
                <td className="py-2 text-xs text-gray-600">{ruleScope(rule)}</td>
                <td className="py-2 text-xs text-gray-600">{rule.enabled ? 'yes' : 'no'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex justify-end">
        <button
          className="bg-blue-primary rounded px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={selected.size === 0 || create.isLoading}
          onClick={launch}
        >
          {create.isLoading ? 'Launching…' : 'Launch Run'}
        </button>
      </div>
    </div>
  );
};

const EvalRunDetail: React.FC<{ runId: number; personaId: string; onClose: () => void }> = ({
  runId,
  personaId,
  onClose,
}) => {
  const cancel = useCancelEvalRun();
  const { data: run, isLoading } = useEvalRun(runId, {
    refetchInterval: (data) => {
      const current = data as EvalRun | undefined;
      const status = current?.status;
      const proposalStatus = current?.proposal?.status;
      if (status === 'running' || status === 'queued') return 2500;
      if (proposalStatus === 'pending' || proposalStatus === 'generating') return 2500;
      return false;
    },
  });

  if (isLoading && !run)
    return (
      <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
        <Spinner alignmentClass="flex items-center justify-center py-8" />
      </div>
    );
  if (!run) return null;

  const isRunning = run.status === 'running' || run.status === 'queued';

  const scenarios: EvalScenarioResult[] = run.results?.scenarios || [];
  const summary = run.summary as Record<string, any> | null | undefined;
  const totals = (summary?.totals as Record<string, any> | undefined) || {};
  const flagRollup = (summary?.flag_rollup as Record<string, number> | undefined) || {};

  const countedFlags = scenarios.reduce((sum, scenario) => {
    const scenarioFlags = scenario.scenario_flags?.length ?? 0;
    const turnFlags = (scenario.turns || []).reduce((turnSum, turn) => turnSum + (turn.flags?.length ?? 0), 0);
    return sum + scenarioFlags + turnFlags;
  }, 0);

  const totalScenarios =
    (totals.scenarios as number | undefined) ?? (summary?.total_scenarios as number | undefined) ?? scenarios.length;
  const totalFlags =
    (totals.flags as number | undefined) ?? (summary?.total_flags as number | undefined) ?? countedFlags;

  const rollupEntries = Object.entries(flagRollup).sort((a, b) => b[1] - a[1]);

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Run #{run.id}</div>
          <div className="text-sm font-semibold text-gray-900">{run.label || `Eval Run ${run.id}`}</div>
          <div className="text-xs text-gray-500">
            Started {formatDate(run.started_at || run.created_at)}
            {run.finished_at && ` · Finished ${formatDate(run.finished_at)}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(run.status)}`}>
            {run.status}
          </span>
          {isRunning && (
            <button
              className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              disabled={cancel.isLoading}
              onClick={() => cancel.mutate(run.id)}
            >
              {cancel.isLoading ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
          <button className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded bg-gray-50 p-2">
          <div className="text-gray-500">Scenarios</div>
          <div className="text-lg font-semibold text-gray-900">{totalScenarios}</div>
        </div>
        <div className="rounded bg-amber-50 p-2">
          <div className="text-amber-700">Flags</div>
          <div className="text-lg font-semibold text-amber-800">{totalFlags}</div>
        </div>
      </div>

      {rollupEntries.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {rollupEntries.map(([name, count]) => (
            <span key={name} className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              {name} ×{count}
            </span>
          ))}
        </div>
      )}

      {run.error && <div className="mt-3 rounded bg-red-50 p-2 text-xs text-red-700">{run.error}</div>}

      {run.proposal?.requested && <ProposalReview personaId={personaId} runId={run.id} proposal={run.proposal} />}

      <div className="mt-4 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Scenarios</div>
        {scenarios.length === 0 && <div className="text-xs text-gray-400">No scenario results yet.</div>}
        {scenarios.map((scenario, scenarioIdx) => (
          <details key={scenario.id ?? scenarioIdx} className="rounded border border-gray-200 bg-gray-50">
            <summary className="cursor-pointer px-3 py-2 text-sm">
              <span className="font-medium">{scenario.id || `Scenario ${scenarioIdx + 1}`}</span>
              {scenario.tags && scenario.tags.length > 0 && (
                <span className="ml-2 text-xs text-gray-500">{scenario.tags.join(', ')}</span>
              )}
            </summary>
            <div className="border-t border-gray-200 bg-white p-3">
              {scenario.notes && <div className="mb-2 text-xs text-gray-600">{scenario.notes}</div>}
              {(scenario.scenario_flags?.length ?? 0) > 0 && (
                <div className="mb-2">
                  <FlagChips flags={scenario.scenario_flags} />
                </div>
              )}
              <div className="space-y-1 text-xs">
                {(scenario.turns || []).map((turn, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="whitespace-pre-wrap rounded bg-blue-50 p-2">
                      <span className="font-semibold uppercase tracking-wider text-blue-600">user</span>{' '}
                      <span className="text-gray-700">{turn.user_message}</span>
                    </div>
                    {turn.ai_response && (
                      <div className="whitespace-pre-wrap rounded bg-gray-50 p-2">
                        <span className="font-semibold uppercase tracking-wider text-gray-500">assistant</span>{' '}
                        <span className="text-gray-700">{turn.ai_response}</span>
                        {turn.tier && <span className="ml-2 text-gray-400">tier {turn.tier}</span>}
                        {turn.intent && <span className="ml-2 text-gray-400">intent {turn.intent}</span>}
                      </div>
                    )}
                    {turn.error && <div className="rounded bg-red-50 p-2 text-red-700">{turn.error}</div>}
                    <FlagChips flags={turn.flags} />
                  </div>
                ))}
              </div>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};

const PersonaEvalsPage: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const { data: runsData, isLoading } = usePersonaEvalRuns(personaId);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  const runs = runsData?.data || [];

  useEffect(() => {
    if (selectedRunId === null && runs.length > 0) setSelectedRunId(runs[0].id);
  }, [runs, selectedRunId]);

  return (
    <PersonaShell personaId={personaId} tab="evals">
      <div className="mx-auto max-w-5xl space-y-6 p-8">
        <EvalLauncher personaId={personaId} onLaunched={(run) => setSelectedRunId(run.id)} />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-1">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">History</h3>
            {isLoading && !runsData ? (
              <Spinner alignmentClass="flex items-center justify-center py-8" />
            ) : (
              <div className="max-h-[70vh] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm">
                {runs.length === 0 && <div className="p-4 text-center text-xs text-gray-400">No eval runs yet.</div>}
                {runs.map((run) => (
                  <button
                    key={run.id}
                    onClick={() => setSelectedRunId(run.id)}
                    className={`block w-full border-b px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                      selectedRunId === run.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{run.label || `Run #${run.id}`}</span>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(run.status)}`}
                      >
                        {run.status}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{formatDate(run.created_at)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            {selectedRunId ? (
              <EvalRunDetail runId={selectedRunId} personaId={personaId} onClose={() => setSelectedRunId(null)} />
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-xs text-gray-500">
                Select a run from the history to view details.
              </div>
            )}
          </div>
        </div>
      </div>
    </PersonaShell>
  );
};

export default PersonaEvalsPage;
